import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { HtmlInputType, ZiroTextComponent, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { ZiroFormConfig } from '../../../forms/ziro-forms-models';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../shared/bottom-nav/bottom-nav.service';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { DubberSettings } from '../../../shared/models/generated/ziroModels';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { ToastService } from '../../../shared/services/toast.service';
import { DubberSettingsContext } from '../../../shared/contexts/dubber-settings.context';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';

@Component({
  selector: 'ziro-dubber-account-management',
  templateUrl: './dubber-management.component.html',
  styleUrls: ['../../../admin/admin-page.scss'],
  standalone: false,
})
export class DubberManagementComponent extends ZiroFormAbstractDirective<DubberSettings> implements OnInit, OnDestroy {
  protected _ziroFormStateService: ZiroFormStateService;
  private _bottomNavService = inject(BottomNavService);
  private _toastService = inject(ToastService);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _dubberSettingsContext = inject(DubberSettingsContext);

  isLoading = true;
  ziroIcons = ZiroIcons;
  formConfig: ZiroFormConfig = {
    fields: {
      username: {
        label: 'tkey;admin.microsoft.dubber_management.username',
        dataAutomation: 'dubber-account-username',
        required: true,
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
      },
      password: {
        label: 'tkey;admin.microsoft.dubber_management.password',
        dataAutomation: 'dubber-account-password',
        required: () => !this.isExisting,
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.PASSWORD }),
      },
      url: {
        label: 'tkey;admin.microsoft.dubber_management.url',
        dataAutomation: 'dubber-account-url',
        required: true,
        componentConfig: new ZiroTextConfig({
          htmlInputType: HtmlInputType.TEXT,
          htmlInputAddOn: { prependedContent: 'https://', appendedContent: '' },
        }),
      },
    },
  };

  private _subscriptions = new Subscription();

  private static _prefixUrl(url: string): string {
    return !url.startsWith('https://') ? 'https://'.concat(url) : url;
  }

  private static _formatUrl(url: string): string {
    return url?.startsWith('https://') ? url.replace(/^https?:\/\//, '') : url;
  }

  ngOnInit(): void {
    this._breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.microsoft.dubber_management.title' }]);
    this._initBottomNav();

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() =>
      this._bottomNavService.setBottomNavValidationError(!this.isFormValid()),
    );
    this._subscriptions.add(formSubmittedSub);

    const ctxSub = this._dubberSettingsContext.state$.subscribe((settings: DubberSettings) => {
      if (settings !== null) {
        this.setIsExisting(this._dubberSettingsContext.isDubberConfigured(settings));
        this.entitySource.next({
          ...settings,
          url: DubberManagementComponent._formatUrl(settings.url),
        });
        this._setDeleteDisabledState(!this.isExisting);
        this.isLoading = false;
      }
    });
    this._subscriptions.add(ctxSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
  }

  protected submit(): Observable<void> {
    return this._onFormSubmit();
  }

  private _onFormSubmit(): Observable<void> {
    this._setSavePendingState(true);
    this._setDeleteDisabledState(true);
    this._setBottomNavValidationError(false);

    const formattedEntity = {
      ...this.entity,
      url: DubberManagementComponent._prefixUrl(this.entity.url),
    };

    return this._dubberSettingsContext.testConnection(formattedEntity).pipe(
      switchMap(() => this._dubberSettingsContext.put(formattedEntity)),
      tap(() => {
        this._toastService.push(
          ToastTypes.SUCCESS,
          this.ziroIcons.DUBBER_LOGO,
          'tkey;shared.toast.save.success.title',
          'tkey;admin.microsoft.dubber_management.toast.save.message',
        );
        this._setSavePendingState(false);
        this._setDeleteDisabledState(false);
        this._resetFormSubmitted();
      }),
      catchError((response) => {
        this._setSavePendingState(false);
        this._setBottomNavValidationError(true);
        throw response;
      }),
    );
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'dubber-management-delete-button',
          dataAutomation: 'dubber-management-delete-button',
          label: 'tkey;dialogs.button.delete',
          buttonClass: ButtonStyles.DANGER,
          state: {
            pending: false,
            buttonDisableState: {
              disabled: false,
              tooltipKey: '',
            },
          },
          icon: this.ziroIcons.DELETE_SMALL,
          cb: () => this._onDeleteClick(),
        },
        {
          id: 'dubber-management-save-button',
          dataAutomation: 'dubber-management-save-button',
          label: 'tkey;admin.ui.save',
          buttonClass: ButtonStyles.PRIMARY,
          state: {
            pending: false,
            buttonDisableState: { disabled: false, tooltipKey: '' },
          },
          icon: this.ziroIcons.SAVE_SUCCESS,
          type: ButtonTypes.SUBMIT,
          submitSubject: this._validateAndSubmitSource,
        },
      ]),
    );
  }

  private _onDeleteClick(): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        modalBodyIconHeaderClass: 'animated bounceIn lead text-center text-danger',
        promptBody: this._translateService.instant('tkey;admin.microsoft.dubber_management.modal.delete.message'),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._deleteDetails(),
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _deleteDetails(): Observable<void> {
    return this._dubberSettingsContext.delete().pipe(
      tap(() => {
        this._resetFormState();
        this._toastService.push(
          ToastTypes.INFO,
          `${this.ziroIcons.DELETE_SMALL} text-danger`,
          'tkey;shared.toast.delete.success.title',
          'tkey;admin.microsoft.dubber_management.toast.delete.message',
        );
      }),
    );
  }

  private _setSavePendingState(setting: boolean): void {
    this._bottomNavService.setButtonPendingState('dubber-management-save-button', setting);
  }

  private _setDeleteDisabledState(state: boolean): void {
    this._bottomNavService.setButtonDisabledState('dubber-management-delete-button', state);
  }

  private _setBottomNavValidationError(hasError: boolean): void {
    this._bottomNavService.setBottomNavValidationError(hasError);
  }

  private _resetFormState(): void {
    this.setIsExisting(false);
    this.entitySource.next({
      username: '',
      password: '',
      url: '',
    });
    this._resetFormSubmitted();
    this._setDeleteDisabledState(true);
  }

  private _resetFormSubmitted(): void {
    this._ziroFormStateService.setIsFormDirty(false);
    this.isFormSubmitted = false;
    this.fieldComponents.forEach((item: ZiroTextComponent) => {
      item.isDirty = false;
      item.showValidation = false;
      item.isFormSubmitted = this.isFormSubmitted;

      if (item.fieldId() === 'password') {
        item.showMaskedInput = false;
        item.applyComponentConfig(new ZiroTextConfig({ htmlInputType: HtmlInputType.PASSWORD }));
      }
    });
  }
}
